import { ChangeDetectionStrategy, Component, ViewChild, computed, input, output } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { TextareaModule } from 'primeng/textarea';
import { Menu, MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { ChatMessageDto } from '@generated/models/chat-message-dto';
import { ChatMessageKind } from '@generated/models/chat-message-kind';
import { ChatRole } from '@generated/models/chat-role';
import { ModelVersionRefDto } from '@generated/models/model-version-ref-dto';

export interface ChatHistoryMetric {
  icon: string;
  tooltip: string;
  value: string;
}

@Component({
  selector: 'app-chat-history-view',
  standalone: true,
  imports: [
    CommonModule, FormsModule, RouterLink,
    TooltipModule, TagModule, ButtonModule, TextareaModule, MenuModule,
    DatePipe,
  ],
  templateUrl: './chat-history-view.component.html',
  styleUrl: './chat-history-view.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatHistoryViewComponent {
  /** Messages to render, in chronological order. */
  readonly messages = input<ChatMessageDto[]>([]);
  /**
   * Fallback display name для Assistant role, если у сообщения нет вложенного
   * `agent.title` (SKN-363). Используется для editable-режимов (seed, benchmark)
   * и legacy-сессий до выкатки. Для новых session.history показывается имя
   * из `msg.agent.title` конкретного турна — оно и переживает switch_agent.
   */
  readonly assistantName = input<string>($localize`:@@chatHistory.agent:Agent`);
  /**
   * SKN-363: builder router-link'а на карточку агента per-turn — parent
   * знает как выглядит route (public/private mode), а мы подставляем id
   * агента из конкретного сообщения (msg.agent.id). Так ссылка ведёт на
   * реального автора реплики, а не на «основного» агента сессии.
   *
   * Если builder не задан ИЛИ у сообщения нет agent — рисуем имя как plain
   * text без ссылки.
   */
  readonly assistantLinkFor = input<((agentId: string) => unknown[] | null) | null>(null);
  /** Show per-message timestamps. */
  readonly showTimestamps = input<boolean>(true);
  /** Show metric badges (latency/timing) when present. */
  readonly showMetrics = input<boolean>(true);
  /** When true, rows are clickable and emit `messageClick` (e.g. to seek audio). */
  readonly clickable = input<boolean>(false);
  /**
   * When true, the user can add/remove/edit message content. Only `Message`-kind
   * bubbles are editable (tool calls / handoffs are not user-creatable).
   * Mutually exclusive with `clickable` in practice (test-edit vs history).
   */
  readonly editable = input<boolean>(false);
  /**
   * IDs of messages currently streaming/transcribing — рисуем спиннер
   * рядом с такими бабблами. Используется live-сессией (agent-test):
   * пока сегмент финализируется, его id лежит здесь.
   */
  readonly streamingMessageIds = input<string[]>([]);
  /**
   * Показывать кнопку «Создать тест отсюда» рядом с каждым User/Assistant
   * сообщением (только в read-only режиме). Клик эмитит `createTestFrom`
   * с этим сообщением — родитель должен решить куда переходить и что
   * взять в seed. PrimeIcons не имеет колбочки — используем pi-check-square
   * (совпадает с иконкой Tests в sidebar'е). */
  readonly testFromHere = input<boolean>(false);

  /** Fired when a row is clicked and `clickable` is true. */
  readonly messageClick = output<ChatMessageDto>();
  /** Fired in `editable` mode whenever the messages array changes. */
  readonly messagesChange = output<ChatMessageDto[]>();
  /** Fired when user clicks the "test from here" button on a message. */
  readonly createTestFrom = output<ChatMessageDto>();

  @ViewChild('addMenu') addMenu?: Menu;

  /** Anchor index for popup `+`-menu. -1 means append at end. */
  private addMenuTargetIndex = -1;

  /** Menu shown by the `+`-button — only User/Assistant for seed conversation. */
  readonly addMenuItems: MenuItem[] = [
    {
      label: $localize`:@@chatHistory.add.user:User message`,
      icon: 'pi pi-user',
      command: () => this.insert('User'),
    },
    {
      label: $localize`:@@chatHistory.add.assistant:Agent message`,
      icon: 'pi pi-microchip',
      command: () => this.insert('Assistant'),
    },
  ];

  /**
   * Pre-computed view model: metrics flattened per message.
   * System messages are hidden in read-only history, but kept in editable
   * mode (seed-conversation builder) so they remain visible and editable.
   */
  readonly rows = computed(() => {
    const editable = this.editable();
    const streamingIds = new Set(this.streamingMessageIds());
    return this.messages()
      .filter(msg => editable || msg.role !== 'System')
      .map(msg => {
        const linkFor = this.assistantLinkFor();
        return {
          msg,
          metrics: this.showMetrics() ? this.buildMetrics(msg) : [],
          argsPretty: this.prettyJson(msg.tool_arguments_json),
          streaming: msg.id != null && streamingIds.has(msg.id),
          // Заголовок assistant-строки: имя агента per-turn если пришло
          // (переживает switch_agent), иначе — fallback из input'а. Пусто
          // у seed-сообщений в editable режимах (test/benchmark).
          assistantHeader: msg.agent?.title ?? this.assistantName(),
          // Router-link ведёт НА того агента, что реально обрабатывал этот
          // конкретный турн (msg.agent.id). Если builder не задан или у
          // msg нет agent — рендерим plain-text (см. template).
          assistantLink: msg.agent?.id && linkFor ? linkFor(msg.agent.id) : null,
          // Engine badge — LLM (classic) или Realtime (v2v). Оба приходят в
          // llm_version (у нас realtime = единый «мозг» с id).
          engineBadge: this.formatEngine(msg.llm_version),
          // Tooltip для engine badge: STT + TTS + VAD в текстовом виде.
          engineTooltip: this.formatEngineTooltip(msg),
        };
      });
  });

  onRowClick(msg: ChatMessageDto): void {
    if (this.clickable() && !this.editable()) this.messageClick.emit(msg);
  }

  // ---- Editable mode ----

  startWith(role: ChatRole): void {
    this.addMenuTargetIndex = -1;
    this.insert(role);
  }

  openAddMenuAfter(index: number, event: Event): void {
    this.addMenuTargetIndex = index;
    this.addMenu?.toggle(event);
  }

  updateContent(index: number, content: string): void {
    const next = this.messages().map((m, i) => i === index ? { ...m, content } : m);
    this.messagesChange.emit(next);
  }

  remove(index: number): void {
    const next = this.messages().filter((_, i) => i !== index);
    this.messagesChange.emit(next);
  }

  private insert(role: ChatRole): void {
    // Стабильный id обязателен: @for (... track msg.id ?? $index) без id
    // фолбэчится на индекс — при inserts/removes середины Angular
    // теряет соответствие DOM ↔ msg и пересоздаёт textarea'ы, из-за
    // чего focus слетает, autoResize перемеряет scrollHeight и
    // .chat-pane прыгает наверх.
    const newMsg: ChatMessageDto = { id: crypto.randomUUID(), role, content: '', kind: 'Message' };
    const current: ChatMessageDto[] = [...this.messages()];
    const after = this.addMenuTargetIndex;
    const at = after === -1 ? current.length : after + 1;
    current.splice(at, 0, newMsg);
    this.addMenuTargetIndex = -1;
    this.messagesChange.emit(current);
  }

  // ---- Helpers ----

  kindFallbackLabel(kind: ChatMessageKind | undefined): string {
    switch (kind) {
      case 'ToolCall': return $localize`:@@chatHistory.kind.toolCall:Tool call`;
      case 'ToolCallResult': return $localize`:@@chatHistory.kind.toolResult:Tool result`;
      case 'AgentHandoff': return $localize`:@@chatHistory.kind.handoff:Agent handoff`;
      default: return '';
    }
  }

  private buildMetrics(msg: ChatMessageDto): ChatHistoryMetric[] {
    const out: ChatHistoryMetric[] = [];
    const m = msg.metrics;
    if (!m) return out;

    // SKN-363: transcript_confidence переехал с top-level ChatMessage в
    // ChatMessageMetrics — рядом с остальными STT-метриками.
    if (m.transcript_confidence != null) {
      out.push({
        icon: 'pi pi-check-circle',
        tooltip: $localize`:@@chatHistory.metric.confidence:Transcript confidence`,
        value: `${Math.round(m.transcript_confidence * 100)}%`,
      });
    }
    if (m.transcription_delay != null) {
      out.push({ icon: 'pi pi-clock', tooltip: $localize`:@@chatHistory.metric.transcriptionDelay:Transcription delay`, value: `${Math.round(m.transcription_delay * 1000)}ms` });
    }
    if (m.end_of_turn_delay != null) {
      out.push({ icon: 'pi pi-stopwatch', tooltip: $localize`:@@chatHistory.metric.endOfTurnDelay:End of turn delay`, value: `${Math.round(m.end_of_turn_delay * 1000)}ms` });
    }
    if (m.on_user_turn_completed_delay != null) {
      out.push({ icon: 'pi pi-arrow-right', tooltip: $localize`:@@chatHistory.metric.userTurnCompleted:On user turn completed delay`, value: `${Math.round(m.on_user_turn_completed_delay * 1000)}ms` });
    }
    if (m.llm_node_ttft != null) {
      out.push({ icon: 'pi pi-bolt', tooltip: $localize`:@@chatHistory.metric.llmTtft:LLM time to first token`, value: `${Math.round(m.llm_node_ttft * 1000)}ms` });
    }
    if (m.tts_node_ttfb != null) {
      out.push({ icon: 'pi pi-volume-up', tooltip: $localize`:@@chatHistory.metric.ttsTtfb:TTS time to first byte`, value: `${Math.round(m.tts_node_ttfb * 1000)}ms` });
    }
    if (m.e2e_latency != null) {
      out.push({ icon: 'pi pi-hourglass', tooltip: $localize`:@@chatHistory.metric.e2eLatency:End-to-end latency`, value: `${Math.round(m.e2e_latency * 1000)}ms` });
    }

    // Preemptive generation (юзерский ход). LiveKit решает её судьбу
    // внутри себя и наружу не отдаёт — воркер снимает исход и кладёт в
    // extra. Показываем всегда, когда спекуляция была: «выброшена» не
    // менее важно, чем «помогла», это оплаченные впустую LLM и TTS.
    if (m.preemptive_used != null) {
      out.push(
        m.preemptive_used
          ? {
              icon: 'pi pi-forward',
              tooltip: $localize`:@@chatHistory.metric.preemptiveUsed:Preemptive generation was used — the reply had a head start of this much`,
              value: m.preemptive_lead_time != null
                ? `+${Math.round(m.preemptive_lead_time * 1000)}ms`
                : $localize`:@@chatHistory.metric.preemptiveUsedNoLead:used`,
            }
          : {
              icon: 'pi pi-ban',
              tooltip: $localize`:@@chatHistory.metric.preemptiveDiscarded:Preemptive generation was discarded — the transcript changed before the turn committed, so the LLM and TTS calls were wasted`,
              value: $localize`:@@chatHistory.metric.preemptiveDiscardedShort:discarded`,
            }
      );
    }

    // Метрики LLM у ответов без текста (чистый tool call). LiveKit их
    // считает, но выбрасывает: вешать не на что, ChatMessage не создаётся.
    // Здесь они приходят с элемента ToolCall — иначе он был бы без единой
    // цифры.
    if (m.llm_ttft != null) {
      out.push({ icon: 'pi pi-bolt', tooltip: $localize`:@@chatHistory.metric.llmTtft:LLM time to first token`, value: `${Math.round(m.llm_ttft * 1000)}ms` });
    }
    if (m.llm_duration != null) {
      out.push({ icon: 'pi pi-clock', tooltip: $localize`:@@chatHistory.metric.llmDuration:LLM request duration`, value: `${Math.round(m.llm_duration * 1000)}ms` });
    }
    if (m.llm_tokens_completion != null || m.llm_tokens_prompt != null) {
      out.push({
        icon: 'pi pi-hashtag',
        tooltip: $localize`:@@chatHistory.metric.llmTokens:LLM tokens — completion / prompt`,
        value: `${m.llm_tokens_completion ?? '—'}/${m.llm_tokens_prompt ?? '—'}`,
      });
    }
    if (m.llm_tps != null) {
      out.push({ icon: 'pi pi-chart-line', tooltip: $localize`:@@chatHistory.metric.llmTps:LLM tokens per second`, value: `${Math.round(m.llm_tps)} t/s` });
    }
    return out;
  }

  private prettyJson(raw: string | null | undefined): string | null {
    if (!raw) return null;
    try { return JSON.stringify(JSON.parse(raw), null, 2); }
    catch { return raw; }
  }

  /**
   * SKN-363: форматируем ModelVersion в компактный лейбл вида
   * `gateway/model · vVERSION` — тот же формат что в telegram-нотификации
   * (см. SendSessionCompletedNotificationCommandHandler.FormatEngine на бэке).
   * Version = `latest` схлопываем (шум).
   */
  private formatEngine(v: ModelVersionRefDto | null | undefined): string | null {
    if (!v) return null;
    const head = [v.gateway, v.model].filter(Boolean).join('/');
    const ver = v.version && v.version.toLowerCase() !== 'latest' ? ` · v${v.version}` : '';
    return `${head}${ver}` || null;
  }

  /**
   * SKN-363: tooltip для engine-badge — раскладка STT + TTS + VAD для
   * classic-pipeline. Realtime-агенты обычно этих refs не имеют (v2v-модель
   * заменяет всю цепочку) — тогда tooltip пуст.
   */
  private formatEngineTooltip(msg: ChatMessageDto): string {
    const parts: string[] = [];
    const stt = this.formatEngine(msg.stt_version);
    const tts = this.formatEngine(msg.tts_version);
    const vad = this.formatEngine(msg.vad_version);
    if (stt) parts.push(`${$localize`:@@chatHistory.engine.stt:STT`}: ${stt}`);
    if (tts) parts.push(`${$localize`:@@chatHistory.engine.tts:TTS`}: ${tts}`);
    if (vad) parts.push(`${$localize`:@@chatHistory.engine.vad:VAD`}: ${vad}`);
    return parts.join('\n');
  }
}
